import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
  BtnPrimary,
  BtnSecondary,
  YamlBlock,
} from "@/components/cathedral/Primitives";

const VENDORS = [
  { id: "mongo", label: "MongoDB Peer" },
  { id: "yaml", label: "YAML Flat-File" },
  { id: "postgres", label: "Postgres (requires DSN)" },
];

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
}

export default function VesselsPage() {
  const [data, setData] = useState(null);
  const [bench, setBench] = useState(null);
  const [vendor, setVendor] = useState("yaml");
  const [label, setLabel] = useState("");
  const [seal, setSeal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [watcher, setWatcher] = useState(null);

  const load = async () => {
    try {
      const [d, w] = await Promise.all([api.vessels(), api.watcherStatus()]);
      setData(d);
      setWatcher(w);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const benchmark = async () => {
    setBusy(true);
    try {
      const b = await api.benchmarkVessels();
      setBench(b);
    } finally {
      setBusy(false);
    }
  };

  const spawn = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.spawnVessel({ vendor, label: label || undefined, seal_current: seal });
      setMsg(`Spawned ${r.vessel.id} (${r.vessel.vendor}).`);
      setLabel("");
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const seal_ = async (id) => {
    setBusy(true);
    try {
      await api.sealVessel(id);
      setMsg(`Sealed ${id}.`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runWatcher = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.watcherRunOnce();
      setMsg(`Watcher · ${r.action?.action} · ${r.action?.vessel_id} · avg=${r.action?.avg_ms ?? "—"}ms`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleWatcher = async () => {
    setBusy(true);
    try {
      if (watcher?.enabled) await api.watcherDisable();
      else await api.watcherEnable();
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="vessels-page" className="px-8 lg:px-12 py-12 max-w-screen-2xl">
      <SectionHeader
        overline="DIR-053 · Technology-Agnostic · DIR-062 · Distributed Continuity"
        title="Vessel Lattice"
        description="When a vessel becomes operationally significant to scan, the lattice seals it and spawns a peer in unison. Reads remain federated across every vessel forever; writes route only to the active vessel."
        action={
          <BtnSecondary onClick={benchmark} disabled={busy} data-testid="vessels-bench-btn">
            {busy ? "Measuring..." : "Benchmark All"}
          </BtnSecondary>
        }
      />

      {msg && (
        <div className="border border-[var(--cyan)] p-4 mb-8 font-mono text-[11px] tracking-widest uppercase bg-[var(--void-2)]">
          {msg}
        </div>
      )}

      {/* Vessel Watcher — auto-spawn when active vessel is full */}
      <Card glow={watcher?.enabled ? "green" : "red"} className="p-7 mb-8" data-testid="vessels-watcher-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <Overline tone={watcher?.enabled ? "green" : "muted"}>
              Vessel Watcher · DIR-053 · Auto-Spawn
            </Overline>
            <h3 className={`font-display text-2xl font-light tracking-[0.04em] mt-3 leading-none ${watcher?.enabled ? "text-glow-green" : "text-glow-red"}`}>
              {watcher?.enabled ? "WATCHER · LIVE" : "WATCHER · PAUSED"}
            </h3>
            <p className="text-sm text-[var(--text-2)] leading-relaxed mt-4 max-w-2xl">
              Benchmarks the active vessel every {watcher?.interval_seconds ?? 300}s. When the seal threshold breaches, the watcher seals the current vessel and spawns a fresh YAML peer — no Architect intervention required.
            </p>
            <div className="mt-4 font-mono text-[10px] text-[var(--text-3)] tracking-[0.22em] uppercase flex flex-wrap gap-4">
              {watcher?.last_run_at && (
                <span>last run · <span className="text-[var(--text)]">{new Date(watcher.last_run_at).toISOString().slice(0, 19)}Z</span></span>
              )}
              {watcher?.last_action?.action && (
                <span>last action · <Tag tone={watcher.last_action.action === "spawned" ? "fact" : watcher.last_action.action === "noop" ? "pass" : "risk"}>{watcher.last_action.action}</Tag></span>
              )}
              {watcher?.last_action?.avg_ms != null && (
                <span>avg · {watcher.last_action.avg_ms}ms</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <BtnPrimary onClick={runWatcher} disabled={busy} data-testid="vessels-watcher-runonce">
              {busy ? "Running…" : "Run Cycle Now"}
            </BtnPrimary>
            <BtnSecondary onClick={toggleWatcher} disabled={busy} data-testid="vessels-watcher-toggle">
              {watcher?.enabled ? "Disable" : "Enable"}
            </BtnSecondary>
          </div>
        </div>
      </Card>

      {/* Spawn a peer */}
      <Card className="p-8 mb-10">
        <Overline>Spawn Peer Vessel</Overline>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            data-testid="vessels-vendor"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            className="border border-[var(--chrome)] rounded-none p-3 font-mono text-[11px] uppercase tracking-widest bg-[var(--void-2)]"
          >
            {VENDORS.map((v) => (
              <option key={v.id} value={v.id}>{v.label}</option>
            ))}
          </select>
          <input
            data-testid="vessels-label"
            placeholder="Label (optional, e.g. 'Cathedral II')"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="md:col-span-2 border border-[var(--chrome)] rounded-none p-3 font-mono text-sm bg-[var(--void-2)]"
          />
          <label className="flex items-center gap-3 border border-[var(--chrome)] p-3 font-mono text-[11px] uppercase tracking-widest cursor-pointer">
            <input
              type="checkbox"
              checked={seal}
              onChange={(e) => setSeal(e.target.checked)}
              className="accent-[var(--cyan)]"
            />
            Seal current
          </label>
        </div>
        <div className="mt-6">
          <BtnPrimary onClick={spawn} disabled={busy} data-testid="vessels-spawn-btn">
            {busy ? "Spawning..." : "Spawn in Unison"}
          </BtnPrimary>
        </div>
      </Card>

      {/* Active vessel banner */}
      {data && (
        <Card className="p-8 mb-10">
          <Overline>Write-Active Vessel</Overline>
          <div className="font-serif text-5xl font-light tracking-tighter mt-3 text-[var(--text)] leading-none">
            {data.active}
          </div>
          <div className="mt-3 font-mono text-[11px] text-[var(--text-3)] tracking-widest uppercase">
            primary · {data.primary} · {data.mounted.length} vessel{data.mounted.length === 1 ? "" : "s"} mounted
          </div>
        </Card>
      )}

      {/* Vessel cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-12">
        {(data?.stats || []).map((v) => (
          <Card key={v.vessel_id} className="p-8" data-testid={`vessel-card-${v.vessel_id}`}>
            <div className="flex items-center justify-between mb-2">
              <Tag tone={v.state === "active" ? "pass" : v.state === "sealed" ? "default" : "assumption"}>
                {(v.state || "—").toUpperCase()}
              </Tag>
              <Tag>{v.vendor?.toUpperCase()}</Tag>
            </div>
            <h3 className="font-serif text-3xl font-light tracking-tighter mt-3 text-[var(--text)] leading-none">
              {v.label || v.vessel_id}
            </h3>
            <div className="font-mono text-[10px] text-[var(--text-3)] mt-2 tracking-widest">
              {v.vessel_id}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 font-mono text-[11px]">
              <div>
                <div className="text-[var(--text-3)] uppercase tracking-widest text-[9px]">scan p95</div>
                <div className="text-[var(--text)] mt-1">
                  {v.p95_latency_ms != null ? `${v.p95_latency_ms} ms` : "—"}
                </div>
              </div>
              <div>
                <div className="text-[var(--text-3)] uppercase tracking-widest text-[9px]">threshold</div>
                <div className="text-[var(--text)] mt-1">{v.scan_threshold_ms || 750} ms</div>
              </div>
              <div>
                <div className="text-[var(--text-3)] uppercase tracking-widest text-[9px]">size</div>
                <div className="text-[var(--text)] mt-1">{fmtBytes(v.data_size_bytes)}</div>
              </div>
              <div>
                <div className="text-[var(--text-3)] uppercase tracking-widest text-[9px]">total docs</div>
                <div className="text-[var(--text)] mt-1">
                  {Object.values(v.collections || {}).reduce((a, b) => a + (b || 0), 0)}
                </div>
              </div>
            </div>

            {v.collections && Object.keys(v.collections).length > 0 && (
              <div className="mt-5 border-t border-[var(--chrome)] pt-4">
                <div className="font-mono text-[9px] text-[var(--text-3)] tracking-widest uppercase mb-2">
                  Collections
                </div>
                <ul className="space-y-1 font-mono text-[11px] text-[var(--text-2)]">
                  {Object.entries(v.collections).map(([k, n]) => (
                    <li key={k} className="flex justify-between">
                      <span>{k}</span>
                      <span className="text-[var(--text-3)]">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {v.state === "active" && v.vessel_id !== data?.primary && (
              <div className="mt-6">
                <BtnSecondary
                  className="!px-4 !py-2 !text-[10px]"
                  onClick={() => seal_(v.vessel_id)}
                  disabled={busy}
                  data-testid={`vessel-seal-${v.vessel_id}`}
                >
                  Seal
                </BtnSecondary>
              </div>
            )}
          </Card>
        ))}
      </div>

      {bench && (
        <Card className="p-8 mt-10">
          <Overline>Latest Benchmark</Overline>
          <YamlBlock className="mt-4" value={JSON.stringify(bench, null, 2)} />
        </Card>
      )}
    </section>
  );
}
